import { ProductPrefix } from "~/utils/constants";

export interface RelationLike {
	Code?: string | null;
}

/** Matches `combine` and `combine-with`, but not `combined-offers`. */
export const matchesRelationPrefix = (
	code: string | null | undefined,
	prefix: ProductPrefix
): boolean => {
	const normalized = code?.trim().toLowerCase();
	if (!normalized) return false;
	return normalized === prefix || normalized.startsWith(`${prefix}-`);
};

/** Every group matching the prefix, in catalogue order. */
export const findRelationsByPrefix = <T extends RelationLike>(
	relations: T[] | null | undefined,
	prefix: ProductPrefix
): T[] =>
	(relations ?? []).filter((relation) =>
		matchesRelationPrefix(relation.Code, prefix)
	);

/**
 * Groups a dedicated block already renders, so generic fallbacks skip them.
 *
 * Only groups whose block is on every page belong here. `combine` is not: the
 * pages that render it pass `SimilarProducts`'s `excludeRelations`, because a
 * template without the block still needs the carousel as its only renderer.
 * `product-variants` is not here either — the carousel may pick it up as a
 * fallback, exactly as it did before this block existed.
 */
export const hasDedicatedBlock = (relation: RelationLike): boolean =>
	Boolean(relation.Code?.includes(ProductPrefix.PACKAGE_PRODUCT));

/** Enough of a relation group to read its items. */
export interface RelationGroupLike<TItem> extends RelationLike {
	Items?: TItem[] | null;
}

/** Items of every group, in order, each product once — one may sit in two. */
export const mergeRelationItems = <TItem extends { Id?: number | null }>(
	groups: RelationGroupLike<TItem>[]
): TItem[] => {
	const seen = new Map<number, TItem>();
	for (const group of groups) {
		for (const item of group.Items ?? []) {
			if (item?.Id != null && !seen.has(item.Id)) seen.set(item.Id, item);
		}
	}
	return [...seen.values()];
};
